import React, { ChangeEvent, FC, memo } from "react";

import { Button } from "@/components/atoms/buttons/Button";
import { ButtonText } from "@/components/atoms/buttons/ButtonText";
import DateFieldBetween from "@/components/atoms/inputForms/DateFieldBetween";
import SearchField from "@/components/atoms/inputForms/SearchField";
import { Select } from "@/components/atoms/inputForms/Select";
import {
  ServiceSearchConditions,
  ServiceStatusOptions,
} from "@/models/service/service";
import { format } from "@/utils/date";
import { ServiceType } from "@/apis/clients";

interface Props {
  serviceSearchConditions: ServiceSearchConditions;
  setServiceSearchConditions: (c: ServiceSearchConditions) => void;
  handleOnChange: (
    ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  handleClear: () => void;
  handleSearch: () => Promise<void>;
}

const ServicesSearchFieldBase: FC<Props> = ({
  serviceSearchConditions,
  setServiceSearchConditions,
  handleOnChange,
  handleClear,
  handleSearch,
}) => {
  const selectedSortType = () => {
    const idx = ServiceStatusOptions.findIndex(
      (o) => o.value === serviceSearchConditions.type,
    );
    if (idx < 0) {
      return { value: "" };
    }
    return ServiceStatusOptions[idx];
  };

  return (
    <div className="md:flex flex-wrap items-end justify-between gap-4">
      <div className="py-4 mx-4 md:py-0 md:mx-0">
        <SearchField
          value={serviceSearchConditions.keyword}
          onChange={handleOnChange}
          placeholder="キーワード検索"
          spRows={1}
          maxLength={255}
          className="h-[44px] border-[1px] !border-[#DFDFE2]"
        />
        <div className="md:flex justify-between flex-wrap gap-x-8 gap-y-4 mt-6">
          <div className="md:flex items-center md:max-w-[400px] w-full gap-x-4 gap-y-2">
            <label className="shrink-0 inline-block text-sm font-bold text-[#1A1A1C] tracking-normal">
              コンテンツタイプ
            </label>
            <Select
              className="min-w-[272px]"
              onChange={(selected) => {
                const p = { ...serviceSearchConditions };
                p.type = selected as unknown as ServiceType;
                setServiceSearchConditions(p);
              }}
              options={ServiceStatusOptions}
              value={selectedSortType().value}
              placeholder="入力なし"
            />
          </div>

          <div className="md:flex items-center">
            <label className="md:mb-0 mb-2 inline-block text-sm font-bold text-[#1A1A1C] md:mr-4">
              更新日
            </label>
            <DateFieldBetween
              className="w-full md:w-40"
              valueFrom={
                serviceSearchConditions.updatedAtFrom
                  ? new Date(serviceSearchConditions.updatedAtFrom)
                  : null
              }
              valueTo={
                serviceSearchConditions.updatedAtTo
                  ? new Date(serviceSearchConditions.updatedAtTo)
                  : null
              }
              onChangeFrom={(selectedDate) => {
                const sd = selectedDate
                  ? format(selectedDate, "yyyy-MM-dd")
                  : undefined;

                const p = { ...serviceSearchConditions };
                typeof sd === "string"
                  ? (p.updatedAtFrom = sd)
                  : (p.updatedAtFrom = "");
                setServiceSearchConditions(p);
              }}
              onChangeTo={(selectedDate) => {
                const sd = selectedDate
                  ? format(selectedDate, "yyyy-MM-dd")
                  : undefined;

                const p = { ...serviceSearchConditions };
                typeof sd === "string"
                  ? (p.updatedAtTo = sd)
                  : (p.updatedAtTo = "");
                setServiceSearchConditions(p);
              }}
            />
          </div>
        </div>
      </div>
      <div className="hidden grow shrink-0 items-center justify-end gap-4 p-4 md:flex md:p-0">
        <ButtonText
          onClick={handleClear}
          label="すべてクリア"
          className="text-[#626262]"
        />
        <Button onClick={handleSearch} label="検索" className="w-[112px]" />
      </div>
    </div>
  );
};

export const ServicesSearchField = memo(ServicesSearchFieldBase);
